import React, { ComponentProps, useCallback, useRef, useState } from 'react';
import { asView } from '../../../_lib/ViewFetcher';
import { ConnectedUser } from './ConnectedUsersDefaultView';
import IForm from '../../../types/IForm';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentList, ContentListItem } from '../../../_lib/components/ContentList';
import { ContactPerson } from '../../../_lib/components/ContactPerson/ContactPerson';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { ConnectedUsersAddForm } from '../partials/ConnectedUserAddForm';
import { ContextMenu, ContextMenuButton } from '../../../_lib/components/ContextMenu';
import { ConnectedUsersDeleteForm } from '../partials/ConnectedUsersDeleteForm';
import { ConnectedUsersEditForm } from '../partials/ConnectedUserEditForm';
import { useDelayed } from '../../../hooks/useDelayed';
import ContentMessage from '../../../_lib/components/ContentMessage/ContentMessage';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { SonForm } from '../../../_lib/Form2';

export type EditableConnectedUser = ConnectedUser & { edit?: IForm; delete?: IForm };
type ConnectUsersEditProps = {
  create: SonForm<any>;
  users: EditableConnectedUser[];
};
function ConnectedUsersEdit({ create, users }: ConnectUsersEditProps) {
  const { t } = useSonTranslation('ConnectedUsers');
  const [activeForm, setActiveForm] = useState<
    { type: 'add' | 'edit' | 'delete'; form: IForm | SonForm<any> } | undefined
  >();
  const { refresh } = useRefreshBlock();
  const ref = useRef<HTMLButtonElement>(null);

  const closeModal = useCallback(() => setActiveForm(undefined), []);
  const delayedClose = useDelayed(closeModal);

  const onSuccess = useCallback(() => {
    refresh();
    delayedClose();
  }, [delayedClose, refresh]);

  const getActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog closeModal={closeModal} headline={t('deleteConnectedUser')} focusOnReturn={ref}>
            <ConnectedUsersDeleteForm onSuccess={onSuccess} form={activeForm.form as IForm} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog
            closeModal={closeModal}
            headline={t('addConnectedUser')}
            focusOnReturn={ref}
            initialFocusIndex={1}
          >
            <ConnectedUsersAddForm form={activeForm.form as SonForm<any>} onSuccess={onSuccess} />
          </ModalDialog>
        );

      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} headline={t('editConnectedUser')} initialFocusIndex={1}>
            <ConnectedUsersEditForm form={activeForm.form as IForm} onSuccess={onSuccess} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar heading={t('addConnectedUser')} hierarchy={3}>
        <BlockBarButton
          ref={ref}
          onClick={() => setActiveForm({ type: 'add', form: create })}
          text={t('addConnectedUser')}
          svgSpriteId={Icons.ADD_PLUS}
        />
      </BlockBar>
      {users.length === 0 ? (
        <ContentMessage text={t('noUsers')} />
      ) : (
        <>
          <Heading hierarchy={4} className="invisible">
            {t('existingConnections')}:
          </Heading>
          <ContentList>
            {users.map(({ role, edit: editForm, delete: deleteForm, ...user }) => (
              <ContentListItem key={user.userId}>
                <ContactPerson
                  roleNames={[role.name]}
                  profileImageUrl={user.profileImageUrl}
                  profileName={user.profileName}
                  phoneLink={user.phoneLink}
                  personInfoCardUrl={user.personInfoCardUrl}
                  profileLink={user.profileLink}
                >
                  {editForm && deleteForm && (
                    <ContextMenu tooltipText={t('contextmenu.tooltip', { name: user.profileName })}>
                      <ContextMenuButton onClick={() => setActiveForm({ type: 'edit', form: editForm })}>
                        {t('contextmenu.edit')}
                      </ContextMenuButton>
                      <ContextMenuButton onClick={() => setActiveForm({ type: 'delete', form: deleteForm })}>
                        {t('contextmenu.delete')}
                      </ContextMenuButton>
                    </ContextMenu>
                  )}
                </ContactPerson>
              </ContentListItem>
            ))}
          </ContentList>
        </>
      )}
      {getActiveForm()}
    </>
  );
}

export const ConnectedUsersEditView = asView(ConnectedUsersEdit);
export type ConnectedUsersEditView = ComponentProps<typeof ConnectedUsersEditView>;
